/**
 * Latest-year income snapshot (ADR-0035 · ADR-0036). Dollar figures come from
 * the newest income period. Margins and EPS prefer the fiscal-year cell, then
 * TTM so a deploy before income_detail_annual.csv is seeded does not blank the
 * panel — but a TTM-sourced cell is never labelled with a fiscal year.
 * Operating income prefers the reported FY `ebit`, then revenue × FY operating
 * margin; it does not mix a fiscal-year revenue with a TTM rate.
 */
import type { IncomePeriod, StockRatios } from '@meridian/contracts';

import { latestServablePeriod, operatingIncome } from './statement-ratios';

export interface IncomeSnapshot {
  readonly year: number | null;
  readonly revenue: number | null;
  readonly netIncome: number | null;
  readonly grossMargin: number | null;
  readonly netMargin: number | null;
  readonly operatingIncome: number | null;
  readonly eps: number | null;
}

const EMPTY: IncomeSnapshot = {
  year: null,
  revenue: null,
  netIncome: null,
  grossMargin: null,
  netMargin: null,
  operatingIncome: null,
  eps: null,
};

function finiteMetric(value: number | null | undefined): number | null {
  if (value === null || value === undefined || !Number.isFinite(value)) return null;
  return value;
}

function preferYear(yearValue: number | null | undefined, ttm: number | null): number | null {
  const fy = finiteMetric(yearValue);
  if (fy !== null) return fy;
  return ttm;
}

function usedTtmFallback(yearValue: number | null | undefined, ttm: number | null): boolean {
  return finiteMetric(yearValue) === null && finiteMetric(ttm) !== null;
}

function incomePeriodServable(period: IncomePeriod): boolean {
  return (
    finiteMetric(period.revenue) !== null ||
    finiteMetric(period.netIncome) !== null ||
    finiteMetric(period.grossMargin) !== null ||
    finiteMetric(period.operatingMargin) !== null ||
    finiteMetric(period.profitMargin) !== null ||
    finiteMetric(period.eps) !== null ||
    finiteMetric(period.ebit) !== null
  );
}

export function incomeSnapshot(
  income: readonly IncomePeriod[],
  ratios: StockRatios,
): IncomeSnapshot {
  const latest = latestServablePeriod(income, incomePeriodServable);
  if (latest === undefined) {
    return {
      ...EMPTY,
      grossMargin: ratios.grossMargin,
      netMargin: ratios.profitMargin,
      eps: ratios.epsTtm,
    };
  }
  const fyOperating = operatingIncome(latest.revenue, finiteMetric(latest.operatingMargin));
  const mixedTtm =
    usedTtmFallback(latest.grossMargin, ratios.grossMargin) ||
    usedTtmFallback(latest.profitMargin, ratios.profitMargin) ||
    usedTtmFallback(latest.eps, ratios.epsTtm);
  return {
    year: mixedTtm ? null : latest.year,
    revenue: latest.revenue,
    netIncome: latest.netIncome,
    grossMargin: preferYear(latest.grossMargin, ratios.grossMargin),
    netMargin: preferYear(latest.profitMargin, ratios.profitMargin),
    operatingIncome: finiteMetric(latest.ebit) ?? fyOperating,
    eps: preferYear(latest.eps, ratios.epsTtm),
  };
}

export function hasIncomeSnapshot(snapshot: IncomeSnapshot): boolean {
  return (
    finiteMetric(snapshot.revenue) !== null ||
    finiteMetric(snapshot.netIncome) !== null ||
    finiteMetric(snapshot.grossMargin) !== null ||
    finiteMetric(snapshot.netMargin) !== null ||
    finiteMetric(snapshot.operatingIncome) !== null ||
    finiteMetric(snapshot.eps) !== null
  );
}
